import {
  Facility,
  FacilityType,
  PeriodTypeEnum,
  RegisterSessionEnum,
  WeeklySchedule,
} from '@schooly/api';
import {
  ceilToHour,
  floorToHour,
  formatMinutesToTime,
  getMinutesSinceMidnight,
} from '@schooly/style';
import { isNotEmpty } from '@schooly/utils/predicates';

export type ScheduleGroupWithRoom = WeeklySchedule['groups'][number] & {
  // undefined if no facilities are available in school
  room?: string[] | null;
};

export type ScheduleItemData = {
  groupsWithRoom: ScheduleGroupWithRoom[];
  onGroupClick?: (groupId: string) => void;
  periodId: string;
  periodType: PeriodTypeEnum;
  roomLabel?: string;
  staffLabel?: string | number;
  registerSession?: RegisterSessionEnum | null;
};

export const DEFAULT_TIME_RANGE = { start: '08:00', end: '18:00' };

export function getScheduleTimeRange(periods: WeeklySchedule['structure']['periods']): {
  start: string;
  end: string;
} {
  const range = periods.reduce(
    (acc, period) => ({
      start:
        getMinutesSinceMidnight(period.start_time) < getMinutesSinceMidnight(acc.start)
          ? period.start_time
          : acc.start,
      end:
        getMinutesSinceMidnight(period.end_time) > getMinutesSinceMidnight(acc.end)
          ? period.end_time
          : acc.end,
    }),
    { start: periods[0].start_time, end: periods[0].end_time },
  );

  return {
    start: floorToHour(range.start),
    end: ceilToHour(range.end),
  };
}

export function extendTimeRange(
  range: { start: string; end: string },
  minHours: number,
): { start: string; end: string } {
  const start = getMinutesSinceMidnight(range.start);
  const end = getMinutesSinceMidnight(range.end);

  if (end - start >= minHours * 60) return range;

  return { start: range.start, end: formatMinutesToTime(start + minHours * 60) };
}

export function buildRoomMap(facilities: Facility[]): Map<string, string[]> {
  const byId = new Map(facilities.map((f) => [f.id, f]));

  return new Map(
    facilities
      .filter((f) => f.type === FacilityType.Room)
      .map((room) => {
        const building = room.parent_id ? byId.get(room.parent_id) : undefined;
        const campus = building?.parent_id ? byId.get(building.parent_id) : undefined;

        const path = [campus?.name, building?.name, room.name].filter(isNotEmpty);
        return [room.id, path];
      }),
  );
}
